import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../../../store/store';
import { fetchProducts } from '../../../../store/ProductSlice';
import ActionLinkButton from '../../../UI/ActionLinkButton/ActionLinkButton';
import { normalizeString, splitToTokensNormalized, scoreFieldsForSearch } from '../../../../utils/searchHelpers';
import './search.scss';

interface SearchProps {
    scrolled: boolean;
    isMobile?: boolean;
}

type SearchItem = {
    id: string;
    name: string;
    slug: string;
    categoryName?: string;
    description?: string;
    fillerTypeName?: string;
    fabricTypeName?: string;
};

const Search: React.FC<SearchProps> = ({ scrolled, isMobile }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [activeIdx, setActiveIdx] = useState(-1);

    const searchRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { products, isLoading } = useSelector((s: RootState) => s.product);

    const toggle = () => setIsOpen((v) => !v);

    const CATALOG_PATH = '/catalog';
    const CATALOG_PARAM = 'q';
    const goToCatalog = () => {
        const raw = query.trim();
        if (raw.length < 2) return;
        navigate(`${CATALOG_PATH}?${CATALOG_PARAM}=${encodeURIComponent(raw)}`);
        setIsOpen(false);
    };

    useEffect(() => {
        if (isOpen) {
            requestAnimationFrame(() => inputRef.current?.focus());
        } else {
            setQuery('');
            setDebouncedQuery('');
            setActiveIdx(-1);
        }
    }, [isOpen]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(normalizeString(query)), 250);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        if (!isOpen) return;
        if (debouncedQuery.length < 2) return;
        if (products.length > 0 || isLoading) return;
        dispatch(fetchProducts() as any);
    }, [isOpen, debouncedQuery, products.length, isLoading, dispatch]);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!isOpen) return;
        const onClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (searchRef.current && !searchRef.current.contains(target)) {
                setIsOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
            document.removeEventListener('keydown', onKey);
        };
    }, [isOpen]);

    const index: SearchItem[] = useMemo(() => {
        return products.map(
            (p) =>
                ({
                    id: p.id,
                    name: p.name || '',
                    slug: p.slug || '',
                    categoryName: p.category?.name || undefined,
                    description: p.nameDescription || p.description || '',
                    fillerTypeName: p.fillerType?.name || '',
                    fabricTypeName: p.fabricType?.name || '',
                }) as SearchItem,
        );
    }, [products]);

    const results = useMemo(() => {
        const q = debouncedQuery;
        if (q.length < 2) return [];
        const tokens = splitToTokensNormalized(q);
        if (!tokens.length) return [];
        const list: { item: SearchItem; score: number }[] = [];
        for (const it of index) {
            const score = scoreFieldsForSearch(
                {
                    name: it.name,
                    description: it.description,
                    fillerType: it.fillerTypeName,
                    fabricType: it.fabricTypeName,
                    category: it.categoryName,
                },
                tokens,
            );
            if (score > 0) {
                list.push({ item: it, score });
            }
        }
        list.sort((a, b) => b.score - a.score);
        return list.slice(0, 6).map((x) => x.item);
    }, [debouncedQuery, index]);

    const showAllVisible = debouncedQuery.length >= 2 && query.trim().length >= 2 && results.length > 0;
    const totalItems = results.length + (showAllVisible ? 1 : 0);
    const showAllIdx = results.length;

    function highlight(name: string): JSX.Element {
        const q = debouncedQuery;
        if (q.length < 2) return <>{name}</>;
        const parts = splitToTokensNormalized(q);
        if (!parts.length) return <>{name}</>;
        const escaped = parts.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const reSplit = new RegExp(`(${escaped.join('|')})`, 'gi');
        const reExact = new RegExp(`^(${escaped.join('|')})$`, 'i');
        const segs = name.split(reSplit);
        return (
            <>
                {segs.map((seg, i) =>
                    reExact.test(seg) ? (
                        <mark key={`${seg}-${i}`} className="header-search__mark">
                            {seg}
                        </mark>
                    ) : (
                        <span key={`${seg}-${i}`}>{seg}</span>
                    ),
                )}
            </>
        );
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown' && totalItems) {
            e.preventDefault();
            setActiveIdx((i) => (i + 1) % totalItems);
            return;
        }
        if (e.key === 'ArrowUp' && totalItems) {
            e.preventDefault();
            setActiveIdx((i) => (i - 1 + totalItems) % totalItems);
            return;
        }
        if (e.key === 'Enter') {
            if (activeIdx >= 0 && activeIdx < results.length) {
                navigate(`/${results[activeIdx].slug}`);
                setIsOpen(false);
                return;
            }
            if (showAllVisible && activeIdx === showAllIdx) {
                goToCatalog();
                return;
            }
            if (activeIdx === -1) {
                goToCatalog();
            }
        }
    };

    return (
        <div className="header-search">
            <ActionLinkButton onClick={toggle} iconSrc="/icons/new/search.svg" iconSrcDark="/icons/mobile/search_w.svg" />
            <div
                id="site-search"
                ref={searchRef}
                className={`header-search__box ${isOpen ? 'header-search__box_open' : ''} ${scrolled ? 'header-search__box_scrolled' : ''}`}
                aria-label="Основное меню"
            >
                <div className="header-search__box_content">
                    <div className="header-search__box_content_wrap">
                        <input
                            ref={inputRef}
                            className="header-search__box_input"
                            type="text"
                            role="combobox"
                            aria-autocomplete="list"
                            aria-haspopup="listbox"
                            placeholder="Поиск"
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setActiveIdx(-1);
                            }}
                            onKeyDown={onKeyDown}
                            autoComplete="off"
                            inputMode="search"
                            aria-expanded={isOpen}
                            aria-controls="search-results"
                        />
                        {!isMobile && (
                            <ActionLinkButton
                                onClick={toggle}
                                iconSrc="/icons/mobile/close.svg"
                                iconSrcDark="/icons/mobile/search_w.svg"
                                className="header-search__box_closeBtn"
                            />
                        )}
                    </div>
                    {isOpen && (
                        <div
                            id="search-results"
                            className={`header-search__results ${results && results.length > 0 ? 'header-search__results_visible' : ''}`}
                            role="listbox"
                            aria-label="Результаты поиска"
                        >
                            {debouncedQuery.length >= 2 && !results.length && <div className="header-search__empty">Ничего не найдено</div>}
                            {results.map((it, idx) => (
                                <Link
                                    key={it.id}
                                    to={`/${it.slug}`}
                                    className={`header-search__item ${idx === activeIdx ? 'header-search__item_active' : ''}`}
                                    role="option"
                                    aria-selected={idx === activeIdx}
                                    onClick={() => setIsOpen(false)}
                                    onMouseEnter={() => setActiveIdx(idx)}
                                >
                                    <div className="header-search__item_title">{highlight(it.name)}</div>
                                    {it.categoryName ? <div className="header-search__item_meta">{it.categoryName}</div> : null}
                                </Link>
                            ))}
                            {showAllVisible && (
                                <Link
                                    key="show-all"
                                    to={`${CATALOG_PATH}?${CATALOG_PARAM}=${encodeURIComponent(query.trim())}`}
                                    className={`header-search__item ${activeIdx === showAllIdx ? 'header-search__item_active' : ''}`}
                                    role="option"
                                    aria-selected={activeIdx === showAllIdx}
                                    onClick={() => setIsOpen(false)}
                                    onMouseEnter={() => setActiveIdx(showAllIdx)}
                                >
                                    Показать все результаты по «{query.trim()}»
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Search;
